import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { leaguesApi } from '../../api/leagues';
import { seasonsApi } from '../../api/seasons';
import { apiClient } from '../../api/client';
import LeagueStatusBadge from '../../components/LeagueStatusBadge';
import { useTranslation } from 'react-i18next';
import { useFeatures } from '../../contexts/FeaturesContext';

function FeatureToggle({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between py-4 border-t border-gray-100 first:border-t-0">
      <span className="text-sm font-semibold text-gray-900">{label}</span>
      <button
        type="button"
        onClick={() => onChange(!value)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${value ? 'bg-primary-800' : 'bg-gray-300'}`}
      >
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${value ? 'translate-x-6' : 'translate-x-1'}`} />
      </button>
    </div>
  );
}

function QuickAction({ to, iconBg, icon, label }: { to: string; iconBg: string; icon: React.ReactNode; label: string }) {
  return (
    <Link to={to} className="flex-1 bg-white border border-gray-100 rounded-2xl p-6 text-center hover:shadow-md transition-shadow group">
      <div className={`w-11 h-11 rounded-xl ${iconBg} flex items-center justify-center mx-auto mb-3.5`}>
        {icon}
      </div>
      <h3 className="font-semibold text-sm text-gray-900 group-hover:text-primary-700">{label}</h3>
    </Link>
  );
}

export default function AdminDashboard() {
  const { t } = useTranslation();
  const { features, reload: reloadFeatures } = useFeatures();
  const qc = useQueryClient();
  const [featureMsg, setFeatureMsg] = useState('');

  const { data: leagues = [], isLoading } = useQuery({
    queryKey: ['admin-leagues'],
    queryFn: () => leaguesApi.list().then((r) => r.data),
    enabled: features.liga,
  });

  const { data: seasons = [] } = useQuery({
    queryKey: ['seasons'],
    queryFn: () => seasonsApi.list().then((r) => r.data),
    enabled: features.saison,
  });

  const [showArchivedSeasons, setShowArchivedSeasons] = useState(false);
  const { data: archivedSeasons = [] } = useQuery({
    queryKey: ['seasons', 'archived'],
    queryFn: () => seasonsApi.list('archived').then((r) => r.data),
    enabled: features.saison && showArchivedSeasons,
  });

  const featureMut = useMutation({
    mutationFn: (data: Record<string, boolean>) => apiClient.put('/api/admin/features', data),
    onSuccess: () => {
      reloadFeatures();
      qc.invalidateQueries({ queryKey: ['admin-leagues'] });
      qc.invalidateQueries({ queryKey: ['seasons'] });
      setFeatureMsg('Gespeichert');
      setTimeout(() => setFeatureMsg(''), 2000);
    },
  });

  const byStatus = {
    draft: leagues.filter((l) => l.status === 'draft'),
    open: leagues.filter((l) => l.status === 'open'),
    active: leagues.filter((l) => l.status === 'active'),
    knockout: leagues.filter((l) => l.status === 'knockout'),
    finished: leagues.filter((l) => l.status === 'finished'),
  };

  const statusLabels: Record<string, string> = {
    draft: t('status.draft'),
    open: t('leagueList.filterOpen'),
    active: t('leagueList.filterActive'),
    knockout: t('leagueList.filterKnockout'),
    finished: t('leagueList.filterFinished'),
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-7 gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-[26px] font-extrabold text-primary-800">{t('admin.dashTitle')}</h1>
          <p className="text-sm text-gray-500 mt-1.5">Verwalte Saisons, Nutzer und Systemeinstellungen.</p>
        </div>
        <div className="flex gap-2">
          {features.saison && (
            <Link to="/admin/seasons/new" className="bg-primary-800 hover:bg-primary-900 text-white font-bold text-sm px-5 py-3 rounded-xl transition-colors">
              + {t('season.newTitle')}
            </Link>
          )}
          {features.liga && (
            <Link to="/admin/leagues/new" className="btn btn-primary btn-sm">
              {t('admin.newLeague')}
            </Link>
          )}
        </div>
      </div>

      {/* Feature toggles */}
      <div className="bg-white border border-gray-100 rounded-2xl px-6 mb-6">
        <div className="flex items-center justify-between pt-5 pb-1">
          <h2 className="font-heading font-semibold text-gray-900">Features</h2>
          {featureMsg && <span className="text-sm text-green-600 font-medium">{featureMsg}</span>}
        </div>
        <FeatureToggle
          label="Liga"
          value={features.liga}
          onChange={(v) => featureMut.mutate({ liga: v })}
        />
        <FeatureToggle
          label="Saison"
          value={features.saison}
          onChange={(v) => featureMut.mutate({ saison: v })}
        />
        <FeatureToggle
          label="Mannschaft"
          value={features.mannschaft}
          onChange={(v) => featureMut.mutate({ mannschaft: v })}
        />
      </div>

      {/* Liga stats */}
      {features.liga && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          {Object.entries(byStatus).map(([status, list]) => (
            <div key={status} className="card text-center">
              <div className="text-3xl font-heading font-extrabold text-primary-800">{list.length}</div>
              <div className="text-sm text-gray-500 mt-1">{statusLabels[status]}</div>
            </div>
          ))}
        </div>
      )}

      {/* Quick actions */}
      <div className="flex flex-col sm:flex-row gap-5 mb-8">
        {features.saison && (
          <QuickAction
            to="/admin/seasons/new"
            iconBg="bg-accent-100"
            icon={<div className="w-4 h-4 rounded bg-accent-500" />}
            label={t('season.newTitle')}
          />
        )}
        <QuickAction
          to="/admin/users"
          iconBg="bg-primary-50"
          icon={
            <svg className="w-5 h-5 text-primary-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          }
          label={t('admin.userManagement')}
        />
        <QuickAction
          to="/admin/audit-log"
          iconBg="bg-green-50"
          icon={
            <svg className="w-5 h-5 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          }
          label={t('admin.auditLog')}
        />
      </div>

      {/* Seasons list */}
      {features.saison && seasons.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-heading font-bold text-primary-800 mb-4">{t('season.listTitle')}</h2>
          <div className="space-y-3">
            {seasons.map((s) => (
              <div key={s.id} className="bg-white border border-gray-100 rounded-2xl px-6 py-5 flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <Link to={`/admin/seasons/${s.id}`} className="font-semibold text-gray-900 hover:text-primary-700 truncate">
                      {s.name}
                    </Link>
                    <span className="badge text-xs bg-primary-50 text-primary-700 border border-primary-200">
                      {t('season.class')} {s.season_class}
                    </span>
                    <span className={`badge text-xs border ${s.status === 'open' ? 'bg-green-100 text-green-700 border-green-300' : s.status === 'active' ? 'bg-primary-50 text-primary-700 border-primary-200' : s.status === 'archived' ? 'bg-purple-100 text-purple-700 border-purple-300' : 'bg-gray-100 text-gray-500 border-gray-300'}`}>
                      {t(`season.status_${s.status}`)}
                    </span>
                  </div>
                  <div className="text-sm text-gray-500 mt-1.5">
                    {s.participant_count ?? 0} {t('season.participants')} · {s.num_rounds} {t('season.rounds')}
                  </div>
                </div>
                <Link to={`/admin/seasons/${s.id}`} className="border border-gray-200 font-semibold text-sm text-primary-800 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors flex-shrink-0">
                  {t('admin.manage')}
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Archived seasons */}
      {features.saison && (
        <div className="mb-8">
          <button
            type="button"
            onClick={() => setShowArchivedSeasons(v => !v)}
            className="text-sm font-semibold text-gray-500 hover:text-gray-700 mb-3"
          >
            {showArchivedSeasons ? '▾' : '▸'} {t('season.archivedSeasons')}
          </button>
          {showArchivedSeasons && (
            <div className="space-y-3">
              {archivedSeasons.length === 0 ? (
                <p className="text-sm text-gray-400">{t('season.noArchivedSeasons')}</p>
              ) : archivedSeasons.map((s) => (
                <div key={s.id} className="bg-white border border-gray-100 rounded-2xl px-6 py-5 flex items-center justify-between gap-4 opacity-70">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2.5 flex-wrap">
                      <Link to={`/admin/seasons/${s.id}`} className="font-semibold text-gray-900 hover:text-primary-700 truncate">
                        {s.name}
                      </Link>
                      <span className="badge text-xs bg-purple-100 text-purple-700 border border-purple-300">
                        {t('season.status_archived')}
                      </span>
                    </div>
                    <div className="text-sm text-gray-500 mt-1.5">
                      {s.participant_count ?? 0} {t('season.participants')} · {s.num_rounds} {t('season.rounds')}
                    </div>
                  </div>
                  <Link to={`/admin/seasons/${s.id}`} className="border border-gray-200 font-semibold text-sm text-primary-800 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors flex-shrink-0">
                    {t('admin.manage')}
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Leagues list */}
      {features.liga && (
        <>
          <h2 className="text-lg font-heading font-bold text-primary-800 mb-4">{t('admin.allLeagues')}</h2>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="card animate-pulse h-16" />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {leagues.map((league) => (
                <div key={league.id} className="bg-white border border-gray-100 rounded-2xl px-6 py-5 flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <Link
                        to={`/admin/leagues/${league.id}`}
                        className="font-semibold text-gray-900 hover:text-primary-700 truncate"
                      >
                        {league.name}
                      </Link>
                      <LeagueStatusBadge status={league.status} />
                    </div>
                    <div className="text-sm text-gray-500 mt-1.5">
                      {t('admin.playersConfirmed', { confirmed: league.confirmed_count ?? 0, total: league.player_count ?? 0 })}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Link to={`/admin/leagues/${league.id}`} className="border border-gray-200 font-semibold text-sm text-primary-800 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors">
                      {t('admin.manage')}
                    </Link>
                    {['open', 'closed', 'draft'].includes(league.status) && (
                      <Link to={`/admin/leagues/${league.id}/draw`} className="btn btn-primary btn-sm">
                        {t('admin.draw')}
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
